import {
  useMediaQuery,
  WhmBox,
  WhmButton,
  WhmDivider,
  WhmSkeleton,
  WhmStack,
  whmTheme,
  WhmTooltip,
  WhmTypography,
} from "@securitize/reactjs-whm";
import {
  useGetAssets,
  useGetPortfolioPerformance,
} from "../../../../../../data/usePortfolio";
import { CurrencyFormat } from "@securitize/reactjs-sid-utils";
import { useContext, useMemo } from "react";
import { iconSmall } from "../../../../../shared/iconThemes";
import { ReactComponent as InfoOutlined } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import StyledCard from "../../../StyledCard";
import { useNavigate } from "react-router";
import Performance from "../../Performance";
import TokenAvatar from "../../TokenAvatar";
import { Translate } from "../../../../../Translation/Translate";
import { translationKeys } from "../../../../../../contexts/I18nContext";
import { TranslationKey } from "../../../../../../contexts/I18nContext/translationKeys";
import { ErrorPage } from "../../../../../shared/ErrorPage";
import { RedirectAuthenticatedContext } from "../../../../../../contexts/RedirectAuthenticatedContext";
import AssetsEmptyState from "./AssetsEmptyState";

function AssetStat({
  labelKey,
  children,
}: {
  labelKey: TranslationKey;
  children: React.ReactNode;
}) {
  return (
    <WhmStack sx={{ flex: 1 }}>
      <WhmTypography variant="body2" color="text.secondary">
        <Translate translationKey={labelKey} />
      </WhmTypography>
      <WhmTypography>{children}</WhmTypography>
    </WhmStack>
  );
}

function AssetCard({
  tokenId,
  tokenName,
  tokenIcon,
  tokenSymbol,
  issuerLogo,
  networkIcon,
  totalUnits,
  lastPrice,
  totalValue,
  pendingUnits,
  performance,
  isPendingIssuance,
  issuerMainCurrency,
  canInvestMore,
  canTrade,
  opportunityId,
  issuerName,
  hasPendingSaft,
  saftTotalValue,
  isPrimaryMarket,
  deploymentId,
  dashboardUrl,
  pendingSubscriptionAmount,
  isPendingSubscription,
}: {
  tokenId: string;
  tokenName: string;
  tokenIcon: string;
  tokenSymbol: string;
  issuerLogo: string;
  networkIcon: string;
  totalUnits: number;
  lastPrice: string;
  totalValue: string;
  pendingUnits?: number;
  performance?: string;
  isPendingIssuance: boolean;
  issuerMainCurrency: string;
  canTrade: boolean;
  canInvestMore: boolean;
  opportunityId?: number;
  issuerName: string;
  hasPendingSaft?: boolean;
  saftTotalValue?: number;
  isPrimaryMarket?: boolean;
  deploymentId?: string;
  dashboardUrl?: string;
  pendingSubscriptionAmount?: number;
  isPendingSubscription?: boolean;
}) {
  const redirectContext = useContext(RedirectAuthenticatedContext);
  const navigate = useNavigate();

  const totalValueFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: issuerMainCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [issuerMainCurrency],
  );

  const currencyFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: issuerMainCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 5,
      }),
    [issuerMainCurrency],
  );

  const totalUnitsFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: "USD", // Ignored
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const isCompactButton = useMediaQuery(
    "@media (min-width: 993px) and (max-width: 1061px)",
  );

  const pendingSubscription = pendingSubscriptionAmount ?? 0;
  // Held position + in-flight subscription on the same asset render as one split card.
  const isSplitDisplay = !isPendingSubscription && pendingSubscription > 0;
  // For subscription-only rows totalValue equals the subscription amount, so it doubles as fallback.
  const subscriptionDisplayAmount = pendingSubscriptionAmount ?? totalValue;
  // totalValue arrives with the subscription amount already added; the settled sub-row shows the rest.
  const settledValue = Number.isFinite(Number(totalValue) - pendingSubscription)
    ? Number(totalValue) - pendingSubscription
    : 0;

  return (
    <StyledCard to={`/asset-details/${tokenId}`}>
      <WhmStack gap="8px">
        {isMobile ? (
          <WhmStack direction="row" gap="16px">
            <WhmStack direction="row" gap="8px" sx={{ flex: 2 }}>
              <TokenAvatar
                tokenIcon={tokenIcon}
                issuerLogo={issuerLogo}
                networkIcon={networkIcon}
              />
              <WhmStack>
                <WhmTooltip title={tokenName}>
                  <WhmTypography
                    fontWeight={500}
                    sx={{
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {tokenName}
                  </WhmTypography>
                </WhmTooltip>
                {isSplitDisplay ? null : (
                  <WhmTypography
                    sx={{
                      color: "text.secondary",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {isPendingSubscription ? (
                      <Translate
                        translationKey={
                          translationKeys.PORTFOLIO_MY_ASSETS_ASSET_SUBSCRIPTION
                        }
                      />
                    ) : (
                      `${totalUnitsFormat.formatNumber(totalUnits)} ${tokenSymbol}`
                    )}
                  </WhmTypography>
                )}
              </WhmStack>
            </WhmStack>

            <WhmStack sx={{ textAlign: "right" }}>
              <WhmTypography>
                {totalValueFormat.format(totalValue)}
              </WhmTypography>
              {performance && !isSplitDisplay ? (
                <Performance value={Number(performance)}>
                  {totalValueFormat.formatNumber(Math.abs(Number(performance)))}
                </Performance>
              ) : null}
            </WhmStack>
          </WhmStack>
        ) : null}
        {isMobile && isSplitDisplay ? (
          <>
            <WhmDivider />
            <WhmStack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
            >
              <WhmTypography variant="body2" color="text.secondary">
                <Translate
                  translationKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_ASSET_VALUE
                  }
                />
              </WhmTypography>
              <WhmStack sx={{ textAlign: "right" }}>
                <WhmTypography>
                  {totalValueFormat.format(settledValue)}
                </WhmTypography>
                <WhmTypography sx={{ color: "text.secondary" }}>
                  {totalUnitsFormat.formatNumber(totalUnits)} {tokenSymbol}
                </WhmTypography>
              </WhmStack>
            </WhmStack>
            <WhmDivider />
            <WhmStack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <WhmTypography variant="body2" color="text.secondary">
                <Translate
                  translationKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_ASSET_SUBSCRIPTION
                  }
                />
              </WhmTypography>
              <WhmTypography>
                {totalValueFormat.format(pendingSubscription)}
              </WhmTypography>
            </WhmStack>
          </>
        ) : null}
        {!isMobile ? (
          <WhmStack
            direction="row"
            gap="16px"
            sx={{ alignItems: isSplitDisplay ? "flex-start" : "center" }}
          >
            <WhmStack
              direction="row"
              gap="8px"
              sx={{ flex: 2, alignItems: "center" }}
            >
              <TokenAvatar
                tokenIcon={tokenIcon}
                issuerLogo={issuerLogo}
                networkIcon={networkIcon}
              />
              <WhmTooltip title={tokenName}>
                <WhmTypography
                  fontWeight={500}
                  sx={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {tokenName}
                </WhmTypography>
              </WhmTooltip>
            </WhmStack>

            {isPendingSubscription ? (
              <WhmStack
                direction="row"
                gap="8px"
                sx={{ flex: 4, minWidth: "450px" }}
              >
                <AssetStat
                  labelKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_ASSET_SUBSCRIPTION
                  }
                >
                  {totalValueFormat.format(subscriptionDisplayAmount)}
                </AssetStat>
              </WhmStack>
            ) : isSplitDisplay ? (
              <WhmStack gap="8px" sx={{ flex: 4, minWidth: "450px" }}>
                <AssetStat
                  labelKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_ASSET_TOTAL_VALUE
                  }
                >
                  {totalValueFormat.format(totalValue)}
                </AssetStat>
                <WhmDivider />
                <WhmStack direction="row" gap="8px">
                  <AssetStat
                    labelKey={translationKeys.PORTFOLIO_MY_ASSETS_ASSET_VALUE}
                  >
                    {totalValueFormat.format(settledValue)}
                  </AssetStat>
                  <AssetStat
                    labelKey={translationKeys.PORTFOLIO_MY_ASSETS_ASSET_UNITS}
                  >
                    {totalUnitsFormat.formatNumber(totalUnits)}
                  </AssetStat>
                  <AssetStat
                    labelKey={translationKeys.PORTFOLIO_MY_ASSETS_ASSET_PRICE}
                  >
                    {currencyFormat.format(lastPrice)}
                  </AssetStat>
                  <WhmStack sx={{ flex: 1 }}>
                    {performance ? (
                      <>
                        <WhmTypography variant="body2" color="text.secondary">
                          <Translate
                            translationKey={
                              translationKeys.PORTFOLIO_MY_ASSETS_LAST_90_DAYS
                            }
                          />
                        </WhmTypography>
                        <Performance value={Number(performance)}>
                          {totalValueFormat.format(
                            Math.abs(Number(performance)),
                          )}
                        </Performance>
                      </>
                    ) : null}
                  </WhmStack>
                </WhmStack>
                <WhmDivider />
                <AssetStat
                  labelKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_ASSET_SUBSCRIPTION
                  }
                >
                  {totalValueFormat.format(pendingSubscription)}
                </AssetStat>
              </WhmStack>
            ) : (
              <WhmStack
                direction="row"
                gap="8px"
                sx={{ flex: 4, minWidth: "450px" }}
              >
                <AssetStat
                  labelKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_ASSET_TOTAL_VALUE
                  }
                >
                  {totalValueFormat.format(totalValue)}
                </AssetStat>
                <AssetStat
                  labelKey={translationKeys.PORTFOLIO_MY_ASSETS_ASSET_UNITS}
                >
                  {totalUnitsFormat.formatNumber(totalUnits)}
                </AssetStat>
                <AssetStat
                  labelKey={translationKeys.PORTFOLIO_MY_ASSETS_ASSET_PRICE}
                >
                  {currencyFormat.format(lastPrice)}
                </AssetStat>
                <WhmStack sx={{ flex: 1 }}>
                  {performance ? (
                    <>
                      <WhmTypography variant="body2" color="text.secondary">
                        <Translate
                          translationKey={
                            translationKeys.PORTFOLIO_MY_ASSETS_LAST_90_DAYS
                          }
                        />
                      </WhmTypography>
                      <Performance value={Number(performance)}>
                        {totalValueFormat.format(Math.abs(Number(performance)))}
                      </Performance>
                    </>
                  ) : null}
                </WhmStack>
              </WhmStack>
            )}

            <WhmBox sx={{ flex: 1, textAlign: "right" }}>
              {canInvestMore ? (
                <WhmButton
                  variant="outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isPrimaryMarket) {
                      navigate(
                        `/primary-market/opportunities/${opportunityId!}`,
                      );
                    } else {
                      const url = new URL(dashboardUrl!);
                      url.hash = `/opportunity/${opportunityId!}`;

                      redirectContext.onRedirect({
                        backUrl: url.toString(),
                        issuer: {
                          name: issuerName,
                          logo: issuerLogo,
                        },
                      });
                    }
                  }}
                >
                  <Translate
                    translationKey={
                      isCompactButton
                        ? translationKeys.PORTFOLIO_MY_ASSETS_ASSET_INVEST_CTA
                        : translationKeys.PORTFOLIO_MY_ASSETS_ASSET_INVEST_MORE_CTA
                    }
                  />
                </WhmButton>
              ) : canTrade ? (
                <WhmButton
                  variant="outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/secondary-market/assets/${deploymentId!}`);
                  }}
                >
                  <Translate
                    translationKey={
                      translationKeys.PORTFOLIO_MY_ASSETS_ASSET_TRADE_CTA
                    }
                  />
                </WhmButton>
              ) : null}
            </WhmBox>
          </WhmStack>
        ) : null}
        {isPendingIssuance && (
          <WhmTypography
            color={(theme) => theme.palette.warning.main}
            variant="body2"
          >
            <Translate
              translationKey={
                translationKeys.PORTFOLIO_MY_ASSETS_PENDING_ISSUANCE
              }
            />{" "}
            <WhmTooltip
              placement="top-start"
              title={
                <Translate
                  translationKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_PENDING_ISSUANCE_TOOLTIP_REGULAR
                  }
                  replacements={{
                    units: pendingUnits
                      ? totalUnitsFormat.formatNumber(pendingUnits)
                      : "",
                  }}
                />
              }
            >
              <WhmBox
                component={InfoOutlined}
                sx={iconSmall}
                fill={whmTheme.palette.action.active}
              />
            </WhmTooltip>
          </WhmTypography>
        )}
        {hasPendingSaft && (
          <WhmTypography
            color={(theme) => theme.palette.warning.main}
            variant="body2"
          >
            <Translate
              translationKey={
                translationKeys.PORTFOLIO_MY_ASSETS_PENDING_ISSUANCE
              }
            />{" "}
            <WhmTooltip
              placement="top-start"
              title={
                <Translate
                  translationKey={
                    translationKeys.PORTFOLIO_MY_ASSETS_PENDING_ISSUANCE_TOOLTIP_TWO_TOKEN
                  }
                  replacements={{
                    amount: saftTotalValue
                      ? totalValueFormat.format(saftTotalValue)
                      : "",
                  }}
                />
              }
            >
              <WhmBox
                component={InfoOutlined}
                sx={iconSmall}
                fill={whmTheme.palette.action.active}
              />
            </WhmTooltip>
          </WhmTypography>
        )}
      </WhmStack>
    </StyledCard>
  );
}

function MyAssetsSkeleton() {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  if (isMobile) {
    return (
      <WhmStack gap="16px">
        {new Array(3).fill(0).map((_, index) => (
          <StyledCard key={index}>
            <WhmStack gap="16px">
              <WhmStack
                direction="row"
                gap="8px"
                sx={{ flex: 2, alignItems: "center" }}
              >
                <WhmSkeleton variant="circular" width={32} height={32} />
                <WhmSkeleton variant="rounded" sx={{ flex: 1 }} height={40} />
                <WhmSkeleton variant="rounded" height={40} sx={{ flex: 1 }} />
              </WhmStack>
            </WhmStack>
          </StyledCard>
        ))}
      </WhmStack>
    );
  } else {
    return (
      <WhmStack gap="16px">
        {new Array(3).fill(0).map((_, index) => (
          <StyledCard key={index}>
            <WhmStack direction="row" gap="16px" sx={{ alignItems: "center" }}>
              <WhmStack
                direction="row"
                gap="8px"
                sx={{ flex: 2, alignItems: "center" }}
              >
                <WhmSkeleton variant="circular" width={32} height={32} />
                <WhmSkeleton variant="rounded" sx={{ flex: 1 }} height={40} />
              </WhmStack>
              <WhmStack direction="row" gap="24px" sx={{ flex: 4 }}>
                <WhmSkeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <WhmSkeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <WhmSkeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <WhmSkeleton variant="rounded" height={40} sx={{ flex: 1 }} />
              </WhmStack>
              <WhmSkeleton variant="rounded" sx={{ flex: 1 }} height={40} />
            </WhmStack>
          </StyledCard>
        ))}
      </WhmStack>
    );
  }
}

export default function MyAssets() {
  const assetsQuery = useGetAssets();
  const performanceQuery = useGetPortfolioPerformance();
  const mergedAssetsQuery = useMemo(() => {
    if (assetsQuery.isSuccess) {
      const mergedData = assetsQuery.data.investments.map((asset) => {
        const performance = performanceQuery.data?.tokens.find(
          (p) => p.tokenId === asset.tokenId,
        );
        return {
          ...asset,
          performance: performance?.value,
        };
      });
      return {
        ...assetsQuery,
        data: { investments: mergedData },
      };
    }

    return assetsQuery;
  }, [assetsQuery, performanceQuery]);

  if (mergedAssetsQuery.isPending) {
    return <MyAssetsSkeleton />;
  }

  if (mergedAssetsQuery.isError) {
    return (
      <ErrorPage
        errorCode={mergedAssetsQuery.error.correlationId}
        onRefresh={() => mergedAssetsQuery.refetch()}
      />
    );
  }

  if (mergedAssetsQuery.data.investments.length === 0) {
    return <AssetsEmptyState />;
  }

  return (
    <WhmStack gap="16px">
      {mergedAssetsQuery.data.investments.map((asset) => (
        <AssetCard key={asset.tokenId} {...asset} />
      ))}
    </WhmStack>
  );
}
